import { z } from 'zod';

import type { Fees, Issues, Route, TokenMetadata } from '../0x.types';
import { EthereumAddress, EthereumHash } from '../models';
import type { EIP712TypedData } from '../signature';
import {
  intentsSubmitApprovalSchema,
  intentsSubmitParamsSchema,
  intentsSubmitTradeSchema,
  intentsStatusRequestSchema,
} from './schema';

/**
 * Native transport types for the standalone 0x Intents API (`/intents/*`).
 *
 * Deliberately independent from the legacy gasless DTOs
 * (`utils/gasless/types.ts`, kept only for rendering persisted history):
 * gasless `JobFailureReason` / `transactions[]` shapes do not match intents'
 * `subStatus` / single `txHash`.
 * See docs/adr/0004-intents-native-dtos-shared-analytics.md.
 */

export enum IntentsApprovalTypes {
  ExecuteMetaTransaction = 'executeMetaTransaction::approve',
  Permit = 'permit',
  DaiPermit = 'daiPermit',
}

export enum IntentsTradeTypes {
  SettlerIntent = 'settler_intent',
  SettlerMetaTransaction = 'settler_metatransaction',
}

/**
 * Contains the necessary information to process an intents approval
 */
export interface IntentsApproval {
  type: IntentsApprovalTypes;
  hash: EthereumHash;
  eip712: EIP712TypedData;
}

/**
 * Intents trade object from the quote response
 */
export interface IntentsTrade {
  type: IntentsTradeTypes;
  hash: EthereumHash;
  eip712: EIP712TypedData;
}

/**
 * `/intents/quote` endpoint object. Same shape as `GaslessQuote` plus
 * `allowanceTarget`, with intents-native trade/approval types (verified live
 * 2026-07-01 and against the 0x-labs intents schemas 2026-07-07).
 */
export interface IntentsQuote {
  allowanceTarget: EthereumAddress | null;
  approval: IntentsApproval | null;
  blockNumber: string;
  buyAmount: string;
  buyToken: EthereumAddress;
  fees: Fees;
  issues: Issues;
  minBuyAmount: string;
  route: Route;
  sellAmount: string;
  sellToken: EthereumAddress;
  target: EthereumAddress;
  trade: IntentsTrade;
  tokenMetadata: TokenMetadata;
  zid: string;
  /**
   * The slippage (bps) governing this quote: echoed client-side from the request's
   * `slippageBps` (absent = server default); a server-resolved value (Model B)
   * overrides the echo when the API starts returning one.
   */
  appliedSlippageBps?: number;
  /** Mode behind the sent value; echoed client-side alongside `appliedSlippageBps`. */
  appliedSlippageMode?: 'auto' | 'manual';
}

/**
 * The Intents API has no /price endpoint; the display-only price is a
 * /intents/quote response. When fetched without a taker (no wallet
 * connected), upstream substitutes an internal preview wallet and returns
 * `trade: null` and `approval: null`, with `issues.balance`/`issues.allowance`
 * nulled as well.
 */
export type IntentsPrice = Omit<IntentsQuote, 'trade'> & { trade: IntentsTrade | null };

export type IntentsSubmitParams = z.infer<typeof intentsSubmitParamsSchema>;

export type IntentsSubmitApproval = z.infer<typeof intentsSubmitApprovalSchema>;

export type IntentsSubmitTrade = z.infer<typeof intentsSubmitTradeSchema>;

export type IntentsStatusRequest = z.infer<typeof intentsStatusRequestSchema>;

export interface IntentsSubmitResponse {
  type: IntentsTradeTypes;
  tradeHash: EthereumHash;
  zid: string;
}

/**
 * `cancelling` = settlement timed out and a cancellation tx is in flight
 * (still pending: the trade can settle or cancel); `cancelled` = the
 * cancellation tx mined - terminal, the trade will never execute.
 */
export const INTENTS_TRADE_STATUSES = [
  'pending',
  'submitted',
  'cancelling',
  'cancelled',
  'succeeded',
  'confirmed',
  'failed',
] as const;

export type IntentsTradeStatus = (typeof INTENTS_TRADE_STATUSES)[number];

/**
 * Failure detail accompanying `status: 'failed'`. The Intents API is the
 * source of truth; values it sends that are missing here surface as warnings
 * via `isKnownIntentsSubStatus` (see `pages/api/intents/status`) instead of
 * silently passing through unmapped.
 */
export enum IntentsSubStatus {
  TransactionSimulationFailed = 'transaction_simulation_failed',
  OrderExpired = 'order_expired',
  TransactionReverted = 'transaction_reverted',
  InsufficientBalance = 'insufficient_balance',
  InsufficientAllowance = 'insufficient_allowance',
  InternalError = 'internal_error',
}

const KNOWN_INTENTS_SUB_STATUSES = new Set<string>(Object.values(IntentsSubStatus));

export function isKnownIntentsSubStatus(subStatus: string): subStatus is IntentsSubStatus {
  return KNOWN_INTENTS_SUB_STATUSES.has(subStatus);
}

/**
 * The response from the intents status endpoint (verified against the
 * 0x-labs intents schemas 2026-07-07). Unlike gasless, a mined trade is
 * reported as a single `txHash`, not a `transactions[]` array, and the
 * tradeHash is not echoed back. Nullable fields arrive as explicit `null`,
 * never omitted.
 */
export type IntentsStatusResponse = {
  status: IntentsTradeStatus;
  /** Non-null only when `status` is `failed`. */
  subStatus: IntentsSubStatus | null;
  chainId: number;
  sellToken: EthereumAddress;
  buyToken: EthereumAddress;
  sellAmount: string;
  minBuyAmount: string;
  quotedBuyAmount: string;
  /** Realized buy amount in base units; null until settled. */
  settledBuyAmount: string | null;
  /** Unix seconds after which a submitted-but-unsettled trade is cancelled. */
  deadline: string;
  // Meta-aggregation fields: the provider that filled the trade and the
  // providers it competed against. ZeroEx-only in phase 1 (ADR-0003).
  winner: string | null;
  competitors: string[];
  txHash: EthereumHash | null;
  zid: string;
};

/**
 * The intents raw stored on a transaction: the submit response, then the
 * latest status snapshot once the watcher polls.
 */
export type IntentsSubmitReceipt = {
  type: IntentsTradeTypes;
  tradeHash: EthereumHash;
  status: IntentsTradeStatus;
  txHash?: EthereumHash | null;
  zid?: string;
};

export function isIntentsReceipt(arg: unknown): arg is IntentsSubmitReceipt {
  if (!arg || typeof arg !== 'object') return false;

  const requiredKeys: (keyof IntentsSubmitReceipt)[] = ['type', 'status', 'tradeHash'];

  // A gasless receipt also carries type/status/tradeHash (including
  // type 'settler_intent' via the interim shim); the absence of its
  // `transactions` array is what identifies an intents receipt.
  return requiredKeys.every((key) => key in arg) && !('transactions' in arg);
}
